import React, { useState, useRef, useEffect } from 'react';
import { Plus, Copy, Trash2, Edit2, Layers, ChevronDown, GripVertical, AlertTriangle, Wand2 } from 'lucide-react';
import { getScreenOverflowElements, getElementLabel } from './utils';

const ScreenTabs = ({
  reviewReadOnly = false,
  screens,
  currentScreenIndex,
  setCurrentScreenIndex,
  editingScreenId,
  editingScreenName,
  setEditingScreenName,
  saveScreenName,
  cancelEditingScreenName,
  startEditingScreenName,
  duplicateScreen,
  deleteScreen,
  deleteAllScreens,
  addNewScreen,
  setShowBulkAddModal,
  draggingScreenIndex,
  dragOverScreenIndex,
  setDragOverScreenIndex,
  handleScreenDragStart,
  handleScreenDragOver,
  handleScreenDrop,
  handleScreenDragEnd,
  fitElementsToCanvas,
  fitAllScreensToCanvas,
}) => {
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const chevronRef = useRef(null);

  // Overflow popover — which screen's warning is hovered, and where to render it
  const [overflowPopover, setOverflowPopover] = useState(null); // { index, top, left }
  const overflowCloseTimer = useRef(null);

  const openOverflowPopover = (index, e) => {
    if (overflowCloseTimer.current) clearTimeout(overflowCloseTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - 272);
    setOverflowPopover({ index, top: rect.bottom + 6, left: Math.max(8, left) });
  };

  const scheduleOverflowClose = () => {
    if (overflowCloseTimer.current) clearTimeout(overflowCloseTimer.current);
    overflowCloseTimer.current = setTimeout(() => setOverflowPopover(null), 120);
  };

  const cancelOverflowClose = () => {
    if (overflowCloseTimer.current) clearTimeout(overflowCloseTimer.current);
  };

  useEffect(() => () => {
    if (overflowCloseTimer.current) clearTimeout(overflowCloseTimer.current);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowAddDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (screens.length === 0) {
    return (
      <div className="bg-gray-50/80 dark:bg-gray-950/40 border-b border-gray-200/80 dark:border-white/[0.06] px-4 py-2.5">
        {!reviewReadOnly && (
        <div className="relative inline-flex" ref={dropdownRef}>
          <button
            onClick={() => { addNewScreen(); setShowAddDropdown(false); }}
            className="btn btn-primary btn-sm rounded-r-none"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Screen
          </button>
          <button
            ref={chevronRef}
            onClick={() => {
              if (!showAddDropdown && chevronRef.current) {
                const rect = chevronRef.current.getBoundingClientRect();
                setDropdownPos({ top: rect.bottom + 4, left: rect.right - 160 });
              }
              setShowAddDropdown(!showAddDropdown);
            }}
            className="btn btn-primary btn-sm rounded-l-none border-l border-white/20 px-1.5"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showAddDropdown && (
            <div 
              className="fixed rounded-xl border border-gray-200 bg-white p-1.5 shadow-float dark:border-white/10 dark:bg-gray-800 min-w-[160px] z-[9999]"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
            >
              <button
                onClick={() => { setShowBulkAddModal(true); setShowAddDropdown(false); }}
                className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors text-left"
              >
                <Layers className="w-4 h-4 text-gray-400" />
                Add Multiple...
              </button>
              {screens.length > 1 && (
                <>
                  <div className="border-t border-gray-100 dark:border-white/[0.06] my-1" />
                  <button
                    onClick={() => { deleteAllScreens(); setShowAddDropdown(false); }}
                    className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10 transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete All Screens
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className="bg-gray-50/80 dark:bg-gray-950/40 border-b border-gray-200/80 dark:border-white/[0.06] px-3 pt-1.5 flex items-end gap-1 overflow-x-auto"
      style={{ scrollbarWidth: 'thin' }}
      onDragOver={(e) => {
        if (draggingScreenIndex === null) return;
        e.preventDefault();
        
        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const relativeX = e.clientX - rect.left;
        
        const tabs = container.querySelectorAll('[data-screen-index]');
        let closestIndex = screens.length;
        let minDistance = Infinity;
        
        tabs.forEach((tab) => {
          const tabRect = tab.getBoundingClientRect();
          const tabCenterX = tabRect.left + tabRect.width / 2 - rect.left;
          const distance = Math.abs(relativeX - tabCenterX);
          
          if (distance < minDistance) {
            minDistance = distance;
            const index = parseInt(tab.getAttribute('data-screen-index'));
            closestIndex = relativeX < tabCenterX ? index : index + 1;
          }
        });
        
        if (closestIndex !== draggingScreenIndex && closestIndex !== dragOverScreenIndex) {
          setDragOverScreenIndex(closestIndex);
        }
      }}
      onDrop={(e) => {
        if (dragOverScreenIndex !== null) {
          handleScreenDrop(e, dragOverScreenIndex);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDragOverScreenIndex(null);
        }
      }}
    >
      {screens.map((screen, index) => {
        const isActive = index === currentScreenIndex;
        const isDragging = draggingScreenIndex === index;
        const showDropBefore = dragOverScreenIndex === index && draggingScreenIndex !== null && draggingScreenIndex !== index && dragOverScreenIndex !== draggingScreenIndex + 1;
        const overflowElements = getScreenOverflowElements(screen);
        const hasOverflow = overflowElements.length > 0;

        return (
          <div key={screen.id} className="relative flex items-end">
            {/* Drop indicator bar */}
            {showDropBefore && (
              <div className="absolute -left-0.5 top-1 bottom-0 w-0.5 bg-primary-500 rounded-full z-10" />
            )}
            
            <div
              data-screen-index={index}
              draggable={!reviewReadOnly && editingScreenId !== screen.id}
              onDragStart={(e) => handleScreenDragStart(e, index)}
              onDragOver={(e) => handleScreenDragOver(e, index)}
              onDrop={(e) => handleScreenDrop(e, index)}
              onDragEnd={handleScreenDragEnd}
              onClick={() => setCurrentScreenIndex(index)}
              className={`group relative flex items-center gap-1.5 pl-2 pr-1.5 py-1.5 rounded-t-lg transition-all duration-150 select-none whitespace-nowrap ${
                isActive
                  ? 'bg-white dark:bg-gray-900 border border-b-0 border-gray-200/80 dark:border-white/[0.08] text-gray-900 dark:text-gray-50 -mb-px z-10'
                  : 'bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100/70 dark:hover:bg-white/[0.04] border border-transparent'
              } ${isDragging ? 'opacity-40 scale-95' : reviewReadOnly ? 'cursor-pointer' : 'cursor-grab'}`}
            >
              {/* Screen number badge */}
              <span className={`flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-md text-[10.5px] font-semibold tabular-nums ${
                isActive
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200/70 dark:bg-white/[0.08] text-gray-600 dark:text-gray-300'
              }`}>
                {index + 1}
              </span>
              
              {/* Screen name */}
              {editingScreenId === screen.id ? (
                <input
                  type="text"
                  value={editingScreenName}
                  onChange={(e) => setEditingScreenName(e.target.value)}
                  onBlur={() => saveScreenName(screen.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveScreenName(screen.id);
                    else if (e.key === 'Escape') cancelEditingScreenName();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  className="text-xs font-medium px-1.5 py-0.5 rounded-md border border-primary-500 bg-white text-gray-900 shadow-focus focus:outline-none dark:bg-gray-900 dark:text-gray-100"
                  style={{ minWidth: '80px', maxWidth: '140px' }}
                />
              ) : (
                <span
                  className={`text-[12.5px] font-medium truncate max-w-[120px] ${isActive ? 'text-gray-900 dark:text-gray-50' : ''}`}
                  onDoubleClick={reviewReadOnly ? undefined : (e) => {
                    e.stopPropagation();
                    startEditingScreenName(screen.id, screen.name);
                  }}
                  title={reviewReadOnly ? screen.name : `${screen.name} — Double-click to rename`}
                >
                  {screen.name}
                </span>
              )}
              
              {/* Overflow warning — content extends past the canvas (hover for details + fix) */}
              {hasOverflow && (
                <span
                  className="flex-shrink-0 inline-flex items-center justify-center w-4 h-4 ml-0.5 text-amber-500 hover:text-amber-600 cursor-help"
                  aria-label={`${overflowElements.length} element(s) outside canvas bounds`}
                  onMouseEnter={(e) => openOverflowPopover(index, e)}
                  onMouseLeave={scheduleOverflowClose}
                  onClick={(e) => e.stopPropagation()}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                </span>
              )}

              {/* Action icons — visible on hover or when active (hidden in review) */}
              {!reviewReadOnly && (
              <div
                className={`flex items-center gap-0.5 ml-0.5 transition-opacity duration-150 ${
                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {editingScreenId !== screen.id && (
                  <button
                    onClick={() => startEditingScreenName(screen.id, screen.name)}
                    className="p-1 rounded-md text-gray-400 hover:bg-gray-200/60 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200 transition-colors"
                    title="Rename"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => duplicateScreen(index)}
                  className="p-1 rounded-md text-gray-400 hover:bg-gray-200/60 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200 transition-colors"
                  title="Duplicate"
                >
                  <Copy className="w-3 h-3" />
                </button>
                {screens.length > 1 && (
                  <button
                    onClick={() => deleteScreen(index)}
                    className="p-1 rounded-md text-gray-400 hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-500/10 dark:hover:text-danger-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              )}

              {/* Active indicator bar */}
              {isActive && (
                <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </div>
          </div>
        );
      })}
      
      {/* Drop zone for end of list */}
      <div 
        className="relative flex items-end"
        onDragOver={(e) => handleScreenDragOver(e, screens.length)}
        onDrop={(e) => handleScreenDrop(e, screens.length)}
      >
        {/* Drop indicator at end */}
        {dragOverScreenIndex === screens.length && draggingScreenIndex !== null && draggingScreenIndex !== screens.length - 1 && (
          <div className="absolute -left-0.5 top-1 bottom-0 w-0.5 bg-primary-500 rounded-full z-10" />
        )}
        
        {!reviewReadOnly && (
        <div className="relative inline-flex ml-1" ref={dropdownRef}>
          <button
            onClick={() => { addNewScreen(); setShowAddDropdown(false); }}
            className="flex items-center gap-1 px-2 py-1.5 mb-1 text-xs font-medium text-gray-500 rounded-lg border border-dashed border-gray-300 hover:border-gray-400 hover:bg-white hover:text-gray-900 dark:border-white/15 dark:text-gray-400 dark:hover:border-white/25 dark:hover:bg-white/[0.04] dark:hover:text-gray-100 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            ref={chevronRef}
            onClick={() => {
              if (!showAddDropdown && chevronRef.current) {
                const rect = chevronRef.current.getBoundingClientRect();
                setDropdownPos({ top: rect.bottom + 4, left: rect.right - 160 });
              }
              setShowAddDropdown(!showAddDropdown);
            }}
            className="px-1 py-1.5 mb-1 text-gray-400 rounded-lg hover:bg-gray-100/70 hover:text-gray-700 dark:hover:bg-white/[0.04] dark:hover:text-gray-200 transition-colors"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showAddDropdown && (
            <div 
              className="fixed rounded-xl border border-gray-200 bg-white p-1.5 shadow-float dark:border-white/10 dark:bg-gray-800 min-w-[160px] z-[9999]"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
            >
              <button
                onClick={() => { setShowBulkAddModal(true); setShowAddDropdown(false); }}
                className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors text-left"
              >
                <Layers className="w-4 h-4 text-gray-400" />
                Add Multiple...
              </button>
              {screens.length > 1 && (
                <>
                  <div className="border-t border-gray-100 dark:border-white/[0.06] my-1" />
                  <button
                    onClick={() => { deleteAllScreens(); setShowAddDropdown(false); }}
                    className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10 transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete All Screens
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Overflow details popover — lists off-canvas elements + one-click fix */}
      {overflowPopover && screens[overflowPopover.index] && (() => {
        const popScreen = screens[overflowPopover.index];
        const popElements = getScreenOverflowElements(popScreen);
        if (popElements.length === 0) return null;
        return (
          <div
            className="fixed rounded-xl border border-gray-200 bg-white py-2 shadow-float dark:border-white/10 dark:bg-gray-800 w-64 z-[9999]"
            style={{ top: overflowPopover.top, left: overflowPopover.left }}
            onMouseEnter={cancelOverflowClose}
            onMouseLeave={scheduleOverflowClose}
          >
            <div className="flex items-center gap-1.5 px-3 pb-2 text-xs font-semibold text-amber-700 dark:text-amber-400 border-b border-gray-100 dark:border-white/[0.06]">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {popElements.length} element{popElements.length !== 1 ? 's' : ''} past the canvas
            </div>
            <ul className="max-h-40 overflow-y-auto py-1">
              {popElements.map((el) => (
                <li
                  key={el.id}
                  className="px-3 py-1 text-xs text-gray-600 dark:text-gray-300 truncate"
                  title={getElementLabel(el)}
                >
                  {getElementLabel(el)}
                </li>
              ))}
            </ul>
            <div className="px-3 pt-2 border-t border-gray-100 dark:border-white/[0.06]">
              <button
                onClick={() => {
                  setCurrentScreenIndex(overflowPopover.index);
                  // 'reposition': rescue only the stray elements. The
                  // 'proportional' mode rescales the entire group, which
                  // shrank perfectly-fine questions until options clipped.
                  fitElementsToCanvas?.('reposition', overflowPopover.index);
                  setOverflowPopover(null);
                }}
                className="btn btn-primary btn-sm w-full"
              >
                <Wand2 className="w-3.5 h-3.5" />
                Auto-fit to canvas
              </button>
              {fitAllScreensToCanvas && (
                <button
                  onClick={() => {
                    fitAllScreensToCanvas('reposition');
                    setOverflowPopover(null);
                  }}
                  className="btn btn-secondary btn-sm mt-1.5 w-full"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto-fit all screens
                </button>
              )}
              <p className="mt-1 text-[10px] leading-tight text-gray-400 dark:text-gray-500 text-center">
                Moves stray elements back onto the {popScreen.layout?.width}×{popScreen.layout?.height}px canvas
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ScreenTabs;
